/**
 * LivretSessionRecap.jsx — Panneau « Compétences vues en session ».
 *
 * Rendu au sommet d'un livret interactif quand l'élève est CONNECTÉ.
 * Lit toutes les auto-évaluations de la trame associée
 * (`code2roues:autoeval-{trameKey}-{phaseId}:v1`) et présente un récap
 * cross-phases avec 3 colonnes :
 *   ✅ Je maîtrise · 🔁 À retravailler · ⏳ Pas encore
 *
 * Objectif : matérialiser dans le livret l'avancement noté lors des
 * sessions sur la trame — SANS dupliquer la donnée, en lecture directe
 * depuis localStorage (ou état sync cloud via useLivretSync).
 *
 * Pour les invités, on affiche un mini-CTA discret « Crée ton compte
 * pour retrouver tes évaluations ici après chaque session ».
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, RotateCcw, AlertCircle, Sparkles, Cloud, UserPlus,
} from 'lucide-react';
import { useApp } from '../../contexts/AppContext';

const STATE_META = {
  ok:   { emoji: '✅', label: 'Je maîtrise',    tone: 'emerald', Icon: CheckCircle2 },
  todo: { emoji: '🔁', label: 'À retravailler', tone: 'amber',   Icon: RotateCcw },
  no:   { emoji: '⏳', label: 'Pas encore',     tone: 'rose',    Icon: AlertCircle },
};

const TONE_BG = {
  emerald: 'bg-emerald-950/25 border-emerald-500/30 text-emerald-100',
  amber:   'bg-amber-950/20 border-amber-500/30 text-amber-100',
  rose:    'bg-rose-950/20 border-rose-500/30 text-rose-100',
};
const TONE_DOT = {
  emerald: 'text-emerald-300',
  amber:   'text-amber-300',
  rose:    'text-rose-300',
};

/**
 * Charge toutes les auto-évaluations sauvegardées localement pour
 * `trameKey`. Retourne un dict { [exId]: 'ok'|'todo'|'no' } fusionné.
 */
function loadAllAutoEvalsForTrame(trameKey) {
  const merged = {};
  const perPhase = {}; // { phaseId: { exId: state } }
  if (typeof window === 'undefined') return { merged, perPhase };
  const prefix = `code2roues:autoeval-${trameKey}-`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix) || !key.endsWith(':v1')) continue;
      const phaseId = key.slice(prefix.length, -':v1'.length);
      try {
        const raw = JSON.parse(localStorage.getItem(key) || '{}');
        perPhase[phaseId] = {};
        Object.entries(raw).forEach(([exId, val]) => {
          if (val === 'ok' || val === 'todo' || val === 'no') {
            merged[exId] = val;
            perPhase[phaseId][exId] = val;
          }
        });
      } catch (_) { /* corrupted entry, skip */ }
    }
  } catch (_) { /* localStorage inaccessible */ }
  return { merged, perPhase };
}

export default function LivretSessionRecap({ trameKey, trameTo, exercises = [] }) {
  const { user } = useApp();

  // On refresh l'état à chaque render pour rester simple. Sur une même
  // page, les updates viennent des autres onglets via l'event storage
  // (le livret ne modifie pas ces clés — lecture seule ici).
  const { merged: autoEval, perPhase } = useMemo(
    () => loadAllAutoEvalsForTrame(trameKey),
    [trameKey],
  );

  if (!trameKey) return null;

  const totalEvaluated = Object.keys(autoEval).length;
  const okList = exercises.filter(ex => autoEval[ex.id] === 'ok');
  const todoList = exercises.filter(ex => autoEval[ex.id] === 'todo');
  const noList = exercises.filter(ex => autoEval[ex.id] === 'no');
  const unratedCount = Math.max(0, exercises.length - totalEvaluated);

  // Cas VIDE — l'élève n'a encore rien évalué.
  if (totalEvaluated === 0) {
    return (
      <section className="rounded-2xl border border-white/10 bg-[#141416] p-4 sm:p-5 mb-6" data-testid="livret-session-recap-empty">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl border border-white/10 bg-white/5 text-white/70 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-0.5">
              Compétences vues en session
            </div>
            <h3 className="text-white font-black text-[15px] leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Rien encore auto-évalué
            </h3>
            <p className="text-white/70 text-[12.5px] mt-1 leading-relaxed">
              À la fin de chaque session sur la trame, scanne le QR élève ou reviens sur la trame et note ton avancement : les compétences maîtrisées apparaîtront ici automatiquement.
            </p>
            {trameTo && (
              <Link
                to={trameTo}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white/85 text-[11px] font-black uppercase tracking-widest transition mt-3"
                data-testid="recap-back-to-trame"
              >
                Ouvrir la trame
              </Link>
            )}
            {!user && (
              <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-950/25 px-3 py-2 flex items-start gap-2">
                <UserPlus className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
                <div className="text-[11.5px] text-emerald-100/90 leading-relaxed">
                  <strong className="text-emerald-200">Crée ton compte</strong> pour que ton auto-évaluation reste ici, même si tu changes de téléphone.
                  <Link to="/register" className="ml-1 underline underline-offset-2 hover:text-white">S'inscrire →</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  const okCount = okList.length;
  const total = exercises.length || totalEvaluated;
  const pct = total > 0 ? Math.round((okCount / total) * 100) : 0;

  return (
    <section className="rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-950/25 via-[#141416] to-[#141416] p-4 sm:p-5 mb-6" data-testid="livret-session-recap">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/15 text-emerald-200">
          <Sparkles className="w-3 h-3" /> Compétences vues en session
        </span>
        {user && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-black uppercase tracking-widest">
            <Cloud className="w-2.5 h-2.5" /> Sync cloud
          </span>
        )}
      </div>

      <h3 className="text-white font-black text-lg sm:text-xl leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Ce que tu as travaillé jusqu'à maintenant
      </h3>
      <p className="text-white/65 text-[12.5px] mt-1 leading-relaxed">
        Récap' automatique de tes auto-évaluations lors des sessions sur la trame. {trameTo && <>Ta note de <em>« Je maîtrise »</em> compte comme <strong className="text-emerald-300">compétence vue en session</strong>.</>}
      </p>

      {/* Barre de progression */}
      <div className="mt-4 mb-4">
        <div className="flex items-center justify-between text-[10.5px] font-black uppercase tracking-widest text-white/60 mb-1.5">
          <span>Maîtrise globale</span>
          <span className="text-emerald-300">{okCount} / {total} · {pct} %</span>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* 3 colonnes de listes */}
      <div className="grid sm:grid-cols-3 gap-2.5">
        {[
          { key: 'ok',   list: okList },
          { key: 'todo', list: todoList },
          { key: 'no',   list: noList },
        ].map(({ key, list }) => {
          const meta = STATE_META[key];
          const Icon = meta.Icon;
          return (
            <div
              key={key}
              className={`rounded-xl border p-3 ${TONE_BG[meta.tone]}`}
              data-testid={`recap-col-${key}`}
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Icon className={`w-3.5 h-3.5 ${TONE_DOT[meta.tone]}`} />
                <span className="text-[10px] font-black uppercase tracking-widest">
                  {meta.label} · {list.length}
                </span>
              </div>
              {list.length > 0 ? (
                <ul className="space-y-1">
                  {list.map(ex => (
                    <li key={ex.id} className="text-[12px] leading-snug flex items-start gap-1.5">
                      <span className={`shrink-0 mt-1 w-1 h-1 rounded-full ${TONE_DOT[meta.tone]}`} style={{ background: 'currentColor' }} />
                      <span>{ex.title}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[11.5px] italic opacity-60">—</p>
              )}
            </div>
          );
        })}
      </div>

      {unratedCount > 0 && (
        <p className="text-white/50 text-[11.5px] mt-3">
          {unratedCount} compétence{unratedCount > 1 ? 's' : ''} pas encore auto-évaluée{unratedCount > 1 ? 's' : ''}.
          {trameTo && (
            <Link to={trameTo} className="ml-1 text-emerald-300 hover:text-emerald-200 underline underline-offset-2">
              Continuer sur la trame →
            </Link>
          )}
        </p>
      )}

      {!user && (
        <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-950/25 px-3 py-2 flex items-start gap-2" data-testid="recap-guest-cta">
          <UserPlus className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
          <div className="text-[11.5px] text-emerald-100/90 leading-relaxed">
            <strong className="text-emerald-200">Crée ton compte</strong> pour retrouver cette progression sur tous tes appareils et la partager avec ton moniteur.
            <Link to="/register" className="ml-1 underline underline-offset-2 hover:text-white">S'inscrire →</Link>
          </div>
        </div>
      )}
    </section>
  );
}
